import React, { useState, useEffect, useCallback } from 'react';
import {
   StoreLogo, CartBtn, TabContent, StoreFooter, 
   AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableText that saves on blur and on Enter
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  // Get the current value from storeTheme first, then from props
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  // Update local value when storeTheme changes or when editing starts
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    // Save the current local value to parent
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    // Deactivate edit mode
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      // Then navigate if provided and not in modal
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    // Save on blur (when clicking outside)
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  // Display the current value - force re-render when storeTheme changes
  const displayValue = p.storeTheme?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function VeloraLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#ff6b6b";
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedLookbookItem, setSelectedLookbookItem] = useState(null);
  const [selectedStylist, setSelectedStylist] = useState(null);
  
  // Force re-render when storeTheme changes
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    // This will trigger re-render when storeTheme updates
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);
  
  // Navigate to page - normal navigation
  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Don't navigate if we're actively editing
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);
  
  // Handle navigation after Enter key in edit mode
  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  // Open blog modal
  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (p.previewEditMode && p.activeEditField) {
      // Deactivate inline edit, then open modal after a short delay
      if (typeof p.onDeactivateField === 'function') {
        p.onDeactivateField();
      }
      setTimeout(() => {
        setSelectedPost(post);
        document.body.style.overflow = 'hidden';
      }, 50);
      return;
    }

    setSelectedPost(post);
    document.body.style.overflow = 'hidden';
  }, [p.previewEditMode, p.activeEditField, p.onDeactivateField]);

  // Close blog modal
  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
    document.body.style.overflow = 'auto';
  }, []);

  // Handle backdrop click
  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedPost(null);
      setSelectedLookbookItem(null);
      setSelectedStylist(null);
      document.body.style.overflow = 'auto';
    }
  }, [p.previewEditMode, p.activeEditField]);
  
  // Auto-rotate hero images
  useEffect(() => {
    const fashionHeroImages = [
       "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32e35743-1600-4df6-b1f9-2b8ef37d8b50_1.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/76d80a10-f428-498e-a615-dd7b06cba415_3.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a32c8344-da63-4169-854b-911b161c31c0_4.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg",
    ];
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % fashionHeroImages.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);
  
  // Animate products flying into view
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 100);
    });
  }, [p.filteredProducts]);
  
  // Mouse move effect for 3D tilt
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  
  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "Velora Winter '24",
    title: p.storeTheme?.heroTitle || "Where fashion finds its voice",
    subtitle: p.storeTheme?.heroSubtitle || "Discover the latest trends in avant-garde fashion.",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop Now",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Collection",
  };
  
  // Fashion hero images
  const fashionHeroImages = [
   "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32e35743-1600-4df6-b1f9-2b8ef37d8b50_1.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/76d80a10-f428-498e-a615-dd7b06cba415_3.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a32c8344-da63-4169-854b-911b161c31c0_4.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg",
  ];
  
  // Blog posts data
  const blogPosts = [
    {
      id: 1,
      titleKey: "blog1_title",
      descKey: "blog1_desc",
      fullContentKey: "blog1_content",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32e35743-1600-4df6-b1f9-2b8ef37d8b50_1.jpg",
      dateKey: "blog1_date",
      authorKey: "blog1_author",
      readTimeKey: "blog1_readtime",
      categoryKey: "blog1_category"
    },
    {
      id: 2,
      titleKey: "blog2_title",
      descKey: "blog2_desc",
      fullContentKey: "blog2_content",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg",
      dateKey: "blog2_date",
      authorKey: "blog2_author",
      readTimeKey: "blog2_readtime",
      categoryKey: "blog2_category"
    },
    {
      id: 3,
      titleKey: "blog3_title",
      descKey: "blog3_desc",
      fullContentKey: "blog3_content",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/76d80a10-f428-498e-a615-dd7b06cba415_3.jpg",
      dateKey: "blog3_date",
      authorKey: "blog3_author",
      readTimeKey: "blog3_readtime",
      categoryKey: "blog3_category"
    }
  ];
  
  // Default blog content
  const defaultBlogContent = {
    blog1_title: "The Art of Layering",
    blog1_desc: "Discover how to master the art of layering this season with our expert styling tips.",
    blog1_content: "Layering is an art form that transforms simple pieces into extraordinary ensembles. Our stylists share insider tips on transitioning your wardrobe through seasons, creating versatile looks that work from desk to dinner.",
    blog1_date: "March 15, 2024",
    blog1_author: "Isabella Rossi",
    blog1_readtime: "5 min read",
    blog1_category: "Style Guide",
    blog2_title: "Sustainable Fashion",
    blog2_desc: "How we're redefining fashion with sustainable practices and ethical sourcing.",
    blog2_content: "True fashion should never come at the expense of our planet. Our commitment to sustainability encompasses every aspect of our production.",
    blog2_date: "March 10, 2024",
    blog2_author: "Marcus Chen",
    blog2_readtime: "4 min read",
    blog2_category: "Sustainability",
    blog3_title: "Behind the Seams",
    blog3_desc: "A glimpse into our atelier where every piece comes to life with love and care.",
    blog3_content: "Step inside our atelier, where creativity meets craftsmanship. Every garment begins as a sketch and takes shape under skilled hands.",
    blog3_date: "March 5, 2024",
    blog3_author: "Elena Vasquez",
    blog3_readtime: "6 min read",
    blog3_category: "Behind the Scenes"
  };
  
  // Custom Sections - Different types of content for each section
  const customSections = [
    { 
      id: "lookbook", 
      title: "Lookbook", 
      icon: "📸", 
      page: "lookbook",
      bgColor: "#ff6b6b",
      textColor: "#ffffff",
      description: "Visual inspiration & styling ideas"
    },
    { 
      id: "stylists", 
      title: "Stylists", 
      icon: "💇‍♀️", 
      page: "stylists",
      bgColor: "#4ecdc4",
      textColor: "#ffffff",
      description: "Meet our expert fashion consultants"
    },
    { 
      id: "events", 
      title: "Events", 
      icon: "🎉", 
      page: "events",
      bgColor: "#ffe66d",
      textColor: "#2d3436",
      description: "Fashion shows & exclusive gatherings"
    }
  ];
  
  // Lookbook Data
  const lookbookItems = [
    { id: 1, image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32e35743-1600-4df6-b1f9-2b8ef37d8b50_1.jpg", title: "Urban Elegance", category: "Street Style", photographer: "Emma Watson" },
    { id: 2, image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg", title: "Summer Breeze", category: "Casual Wear", photographer: "James Wilson" },
    { id: 3, image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/76d80a10-f428-498e-a615-dd7b06cba415_3.jpg", title: "Evening Glam", category: "Formal", photographer: "Sofia Rodriguez" },
    { id: 4, image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a32c8344-da63-4169-854b-911b161c31c0_4.jpg", title: "Bohemian Rhapsody", category: "Festival", photographer: "Lucas Chen" },
    { id: 5, image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg", title: "Minimalist Chic", category: "Everyday", photographer: "Aisha Khan" },
    { id: 6, image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e3abbc7b-a006-4b9f-ba0a-e1ba8e0a6cfe_13.jpg", title: "Vintage Vibes", category: "Retro", photographer: "Oliver Smith" },
  ];
  
  // Stylists Data
  const stylists = [
    { id: 1, name: "Isabella Rossi", role: "Head Stylist", expertise: "Evening Wear, Red Carpet", image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg", bio: "With over 10 years of experience in luxury fashion, Isabella has styled celebrities for major events worldwide.", instagram: "@isabella_styles", available: true },
    { id: 2, name: "Marcus Chen", role: "Creative Director", expertise: "Streetwear, Editorial", image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a32c8344-da63-4169-854b-911b161c31c0_4.jpg", bio: "Marcus brings urban edge to high fashion, known for his innovative layering techniques and bold color combinations.", instagram: "@marcus_chen", available: true },
    { id: 3, name: "Sofia Vasquez", role: "Personal Shopper", expertise: "Everyday Style, Workwear", image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg", bio: "Sofia specializes in curating versatile wardrobes that transition seamlessly from desk to dinner.", instagram: "@sofia_styles", available: false },
    { id: 4, name: "Aisha Kapoor", role: "Sustainable Fashion Expert", expertise: "Eco-fashion, Upcycling", image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/76d80a10-f428-498e-a615-dd7b06cba415_3.jpg", bio: "Aisha is passionate about making sustainable fashion accessible and stylish for everyone.", instagram: "@aisha_eco", available: true },
  ];
  
  // Events Data
  const events = [
    { id: 1, title: "Spring/Summer '25 Show", date: "June 15, 2025", location: "Paris Fashion Week", time: "7:00 PM", image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg", description: "Experience our latest collection on the runway." },
    { id: 2, title: "Velora Pop-up Shop", date: "July 1-7, 2025", location: "New York City", time: "10:00 AM - 8:00 PM", image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg", description: "Exclusive in-person shopping experience." },
    { id: 3, title: "Style Masterclass", date: "August 5, 2025", location: "Virtual Event", time: "3:00 PM EST", image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a32c8344-da63-4169-854b-911b161c31c0_4.jpg", description: "Learn styling tips from our expert team." },
    { id: 4, title: "Sustainable Fashion Summit", date: "September 20, 2025", location: "London", time: "9:00 AM - 6:00 PM", image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e3abbc7b-a006-4b9f-ba0a-e1ba8e0a6cfe_13.jpg", description: "Join us in discussing the future of fashion." },
  ];
  
  // Fashion categories
  const fashionCategories = [
    { key: "cat1", nameKey: "category_ready_to_wear", icon: "👗", color: "#ff6b6b", itemsKey: "category_ready_to_wear_items", defaultName: "READY TO WEAR", defaultItems: "234 items" },
    { key: "cat2", nameKey: "category_accessories", icon: "👜", color: "#4ecdc4", itemsKey: "category_accessories_items", defaultName: "ACCESSORIES", defaultItems: "156 items" },
    { key: "cat3", nameKey: "category_footwear", icon: "👠", color: "#ffe66d", itemsKey: "category_footwear_items", defaultName: "FOOTWEAR", defaultItems: "98 items" },
    { key: "cat4", nameKey: "category_sustainable", icon: "🌿", color: "#95e77e", itemsKey: "category_sustainable_items", defaultName: "SUSTAINABLE", defaultItems: "67 items" },
  ];
  
  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_lookbook", label: "LOOKBOOK", page: "lookbook" },
    { key: "nav_stylists", label: "STYLISTS", page: "stylists" },
    { key: "nav_events", label: "EVENTS", page: "events" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];
  
  // Top bar messages
  const topBarMessages = [
    "FREE SHIPPING ON ORDERS OVER ₹4999",
    "30-DAY EASY RETURNS",
    "SUSTAINABLE FASHION",
    "NEW ARRIVALS EVERY WEEK"
  ];
  
  // Particles
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 2 + Math.random() * 6,
    delay: Math.random() * 5,
    duration: 3 + Math.random() * 4,
  }));

  // Determine if we should use dark text based on scroll position and active page
  const shouldUseDarkText = isScrolled || activePage !== "home";

  return (
    <div key={refreshKey} className="w-full min-h-screen flex flex-col bg-[#f7fff7] overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Montserrat', sans-serif" }}>
      
      {/* Floating Particles Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(30 + Math.random() * 50).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar */}
      <div className="relative bg-[#1a1a2e] text-white py-2 overflow-hidden">
        <div className="whitespace-nowrap animate-marquee">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-8">
              ✨ <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 w-full ${shouldUseDarkText ? "bg-white/95 backdrop-blur-xl shadow-lg py-3" : "bg-transparent py-5"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          {/* Logo */}
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={(e) => navigateTo("home", e)}
          >
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${shouldUseDarkText ? "border-[#ff6b6b]" : "border-white"}`} />
            <InlineEditableTextWithNav 
              p={p} 
              field="businessName" 
              value={p.storeInfo?.businessName || "Velora"} 
              className={`font-bold text-xl tracking-tight transition-colors ${shouldUseDarkText ? "text-[#1a1a2e]" : "text-white"}`}
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className={`w-6 h-6 ${shouldUseDarkText ? "text-[#1a1a2e]" : "text-white"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 hover:scale-105 relative group ${activePage === item.page ? "text-[#ff6b6b]" : (shouldUseDarkText ? "text-gray-700" : "text-white")}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#ff6b6b] transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className={`p-2 rounded-full transition-all duration-300 ${shouldUseDarkText ? "hover:bg-gray-100" : "hover:bg-white/10"}`}>
                <FaHeart className={`text-lg transition-colors ${shouldUseDarkText ? "text-[#ff6b6b]" : "text-white"}`} />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#ff6b6b] text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={!shouldUseDarkText} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 flex flex-col gap-2 z-50">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${activePage === item.page ? "bg-[#ff6b6b]/10 text-[#ff6b6b]" : "text-gray-700"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[72px]">
            <div className="absolute inset-0 w-full">
              {fashionHeroImages.map((img, idx) => (
                <div key={idx} className={`absolute inset-0 w-full transition-opacity duration-1000 ease-in-out ${idx === activeSlide ? "opacity-100" : "opacity-0"}`}>
                  <img src={img} alt={`Fashion ${idx + 1}`} className="w-full h-full object-cover object-center" style={{ transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px) scale(1.05)`, transition: "transform 0.3s ease-out" }} />
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
            </div>
            
            <div className="relative h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-2xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                    <span className="w-2 h-2 rounded-full bg-[#ff6b6b] animate-ping" />
                    <span className="text-xs tracking-[0.2em] text-white/90">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-[#4ecdc4] animate-pulse" />
                  </div>
                </div>
                <h1 className="text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.1] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-white/80 text-base md:text-lg max-w-lg animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("lookbook", e)} 
                    className="group px-6 md:px-8 py-3 md:py-4 rounded-full bg-[#ff6b6b] text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#ff6b6b]/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value="Explore Lookbook"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="lookbook"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("stylists", e)} 
                    className="px-6 md:px-8 py-3 md:py-4 rounded-full border-2 border-white/30 text-white font-semibold transition-all duration-300 hover:border-white/60 hover:bg-white/10 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value="Meet Stylists"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="stylists"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
              <div className="flex flex-col items-center gap-1">
                <span className="text-white/50 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-white/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-white/50 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Custom Sections Showcase on Home Page */}
          <section className="relative w-full px-4 md:px-8 py-16">
            <div className="text-center mb-12">
              <span className="text-[#ff6b6b] text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="sections_badge" 
                  value="Explore"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="sections_title" 
                  value="Discover Velora"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#ff6b6b] to-[#4ecdc4] mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {customSections.map((section, idx) => {
                return (
                  <div 
                    key={section.id} 
                    className="group cursor-pointer animate-card-fly" 
                    style={{ animationDelay: `${idx * 0.1}s` }} 
                    onClick={(e) => navigateTo(section.page, e)}
                  >
                    <div 
                      className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                      style={{ backgroundColor: section.bgColor }}
                    >
                      <div className="p-8 text-center">
                        <div className="text-5xl mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                          {section.icon}
                        </div>
                        <h3 className="font-bold text-xl mb-2" style={{ color: section.textColor }}>
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={`${section.id}_title`} 
                            value={section.title}
                            onNavigate={handleNavigateAfterEdit}
                            navPage={section.page}
                          />
                        </h3>
                        <p className="text-sm opacity-90" style={{ color: section.textColor }}>
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={`${section.id}_desc`} 
                            value={section.description}
                            onNavigate={handleNavigateAfterEdit}
                            navPage={section.page}
                          />
                        </p>
                        <div className="mt-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <span className="text-xs font-semibold" style={{ color: section.textColor }}>
                            Explore →
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          
          {/* Category Cards - Keep these for product browsing */}
          <section className="relative w-full px-4 md:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {fashionCategories.map((category, idx) => (
                <div 
                  key={category.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.15}s` }} 
                  onClick={(e) => {
                    p.setActiveTab("products");
                    navigateTo("home", e);
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                    <div className="p-6 text-center">
                      <div className="text-4xl mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12" style={{ color: category.color }}>
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={`${category.key}_icon`} 
                          value={category.icon}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="home"
                        />
                      </div>
                      <h3 className="font-bold text-[#1a1a2e]">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={category.nameKey} 
                          value={category.defaultName}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="home"
                        />
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={category.itemsKey} 
                          value={category.defaultItems}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="home"
                        />
                      </p>
                      <div className="mt-3 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: category.color }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== LOOKBOOK PAGE ==================== */}
      {activePage === "lookbook" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3">
              <div className="w-8 h-px bg-[#ff6b6b]" />
              <span className="text-[#ff6b6b] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="lookbook_badge" 
                  value="Visual Inspiration"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="lookbook"
                />
              </span>
              <div className="w-8 h-px bg-[#ff6b6b]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a2e]">
              <InlineEditableTextWithNav 
                p={p} 
                field="lookbook_title" 
                value="Style Lookbook"
                onNavigate={handleNavigateAfterEdit}
                navPage="lookbook"
              />
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="lookbook_subtitle" 
                value="Discover how to style our pieces for every occasion"
                onNavigate={handleNavigateAfterEdit}
                navPage="lookbook"
              />
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-[#ff6b6b] to-[#4ecdc4] mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lookbookItems.map((item) => (
              <div 
                key={item.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedLookbookItem(item)}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end p-6">
                    <div className="text-white">
                      <p className="text-xs uppercase tracking-wider text-[#ff6b6b]">{item.category}</p>
                      <h3 className="text-xl font-bold mt-1">{item.title}</h3>
                      <p className="text-sm opacity-80 mt-2">📸 {item.photographer}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== STYLISTS PAGE ==================== */}
      {activePage === "stylists" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3">
              <div className="w-8 h-px bg-[#4ecdc4]" />
              <span className="text-[#4ecdc4] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="stylists_badge" 
                  value="Expert Team"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="stylists"
                />
              </span>
              <div className="w-8 h-px bg-[#4ecdc4]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a2e]">
              <InlineEditableTextWithNav 
                p={p} 
                field="stylists_title" 
                value="Meet Our Stylists"
                onNavigate={handleNavigateAfterEdit}
                navPage="stylists"
              />
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="stylists_subtitle" 
                value="Our expert fashion consultants are here to inspire you"
                onNavigate={handleNavigateAfterEdit}
                navPage="stylists"
              />
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-[#4ecdc4] to-[#95e77e] mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stylists.map((stylist) => (
              <div 
                key={stylist.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={() => setSelectedStylist(stylist)}
              >
                <div className="h-64 overflow-hidden">
                  <img src={stylist.image} alt={stylist.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold text-[#1a1a2e]">{stylist.name}</h3>
                  <p className="text-[#4ecdc4] text-sm font-semibold mt-1">{stylist.role}</p>
                  <p className="text-xs text-gray-500 mt-2">{stylist.expertise}</p>
                  <div className="mt-4">
                    <span className="text-xs text-gray-400">{stylist.instagram}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== EVENTS PAGE ==================== */}
      {activePage === "events" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3">
              <div className="w-8 h-px bg-[#ffe66d]" />
              <span className="text-[#ffe66d] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="events_badge" 
                  value="Upcoming"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="events"
                />
              </span>
              <div className="w-8 h-px bg-[#ffe66d]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a2e]">
              <InlineEditableTextWithNav 
                p={p} 
                field="events_title" 
                value="Fashion Events"
                onNavigate={handleNavigateAfterEdit}
                navPage="events"
              />
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="events_subtitle" 
                value="Join us for exclusive fashion experiences and gatherings"
                onNavigate={handleNavigateAfterEdit}
                navPage="events"
              />
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-[#ffe66d] to-[#fdcb6e] mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {events.map((event) => (
              <div key={event.id} className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="h-64 overflow-hidden">
                  <img src={event.image} alt={event.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-400">{event.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a2e] mb-2">{event.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span>📍 {event.location}</span>
                    <span>⏰ {event.time}</span>
                  </div>
                  <p className="text-gray-600">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#ff6b6b] text-sm tracking-wider uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Our Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a2e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Latest Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#ff6b6b] to-[#4ecdc4] mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <img src={post.image} alt="Blog" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#ff6b6b] font-semibold">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.categoryKey} 
                        value={defaultBlogContent[post.categoryKey]}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.readTimeKey} 
                        value={defaultBlogContent[post.readTimeKey]}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.dateKey} 
                      value={defaultBlogContent[post.dateKey]}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <h3 className="text-xl font-semibold text-[#1a1a2e] mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={defaultBlogContent[post.titleKey]}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-500 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={defaultBlogContent[post.descKey]}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.authorKey} 
                        value={defaultBlogContent[post.authorKey]}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-[#ff6b6b] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#ff6b6b] text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="Our Story"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a2e] mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="A Legacy of Style"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-0.5 bg-[#ff6b6b] mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Founded with a passion for fashion, Velora represents the pinnacle of contemporary style. Each piece is designed with the modern individual in mind."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="Our commitment to quality and sustainability is reflected in every garment we create. From eco-friendly fabrics to ethical production, we're building a better future for fashion."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-[#ff6b6b]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="500+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Collections"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#ff6b6b]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Designers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#ff6b6b]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="100k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src=    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg" alt="About Velora" className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      )}
      
      {/* Style Inspiration Section */}
      {activePage === "home" && (
        <section className="mt-20 relative overflow-hidden w-full">
          <div className="absolute inset-0 w-full">
            <img src="https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e3abbc7b-a006-4b9f-ba0a-e1ba8e0a6cfe_13.jpg" alt="Style inspiration" className="w-full h-full object-cover" style={{ transform: `scale(1.1) translateY(${mousePosition.y * 0.5}px)`, transition: "transform 0.3s ease-out" }} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30" />
          </div>
          <div className="relative w-full px-4 md:px-8 py-12 md:py-20 text-center">
            <div className="animate-float">
              <InlineEditableTextWithNav 
                p={p} 
                field="inspiration_icon" 
                value="✨"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4">
              <InlineEditableTextWithNav 
                p={p} 
                field="inspiration_title" 
                value="Style Inspiration"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </h2>
            <p className="text-white/80 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="inspiration_subtitle" 
                value="Discover how to style our latest collection with our lookbook"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </p>
            <button 
              onClick={(e) => navigateTo("lookbook", e)} 
              className="mt-8 px-6 md:px-8 py-2 md:py-3 rounded-full bg-white text-[#1a1a2e] font-semibold hover:bg-[#ff6b6b] hover:text-white transition-all duration-300"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="explore_lookbook_button" 
                value="Explore Lookbook"
                onNavigate={handleNavigateAfterEdit}
                navPage="lookbook"
              />
            </button>
          </div>
        </section>
      )}
      
      {/* Tabs - Show product tabs on all pages */}
      <section className="w-full px-4 md:px-8 mt-12">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "SHOP COLLECTION"],
            ["services", "SERVICES"],
            ["details", "ABOUT STORE"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-4 md:px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-[#ff6b6b] text-white shadow-lg" : "text-gray-500 hover:text-[#ff6b6b]"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content - Products/Services/Details */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      {/* Newsletter */}
      <section className="bg-[#1a1a2e] py-12 md:py-16 mt-8 w-full">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-[#ff6b6b] text-sm tracking-wider uppercase">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_badge" 
              value="Subscribe"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </span>
          <h3 className="text-xl md:text-2xl font-bold text-white mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_title" 
              value="Join the Velora Community"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </h3>
          <p className="text-gray-400 text-sm mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_subtitle" 
              value="Get exclusive access to new collections and offers"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
              className="flex-1 px-5 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/30 focus:border-[#ff6b6b] outline-none transition" 
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-[#ff6b6b] text-white font-semibold hover:bg-white hover:text-[#ff6b6b] transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="subscribe_button" 
                value="Subscribe"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </button>
          </div>
        </div>
      </section>
      
      <StoreFooter p={p} />
      
      {/* Lookbook Modal */}
      {selectedLookbookItem && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-4xl w-full bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img src={selectedLookbookItem.image} alt={selectedLookbookItem.title} className="w-full h-auto" />
              <button 
                onClick={() => setSelectedLookbookItem(null)} 
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-[#ff6b6b] transition flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-[#1a1a2e]">{selectedLookbookItem.title}</h3>
              <p className="text-[#ff6b6b] text-sm mt-1">{selectedLookbookItem.category}</p>
              <p className="text-gray-600 mt-3">Photography by {selectedLookbookItem.photographer}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Stylist Modal */}
      {selectedStylist && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="grid md:grid-cols-2">
              <img src={selectedStylist.image} alt={selectedStylist.name} className="w-full h-full object-cover" />
              <div className="p-6">
                <div className="flex justify-end">
                  <button onClick={() => setSelectedStylist(null)} className="text-gray-400 hover:text-[#ff6b6b] text-2xl">✕</button>
                </div>
                <h3 className="text-2xl font-bold text-[#1a1a2e]">{selectedStylist.name}</h3>
                <p className="text-[#4ecdc4] font-semibold mt-1">{selectedStylist.role}</p>
                <p className="text-gray-600 mt-3">{selectedStylist.bio}</p>
                <p className="text-sm text-gray-500 mt-3">📧 {selectedStylist.instagram}</p>
                <p className="text-sm text-gray-500 mt-1">✨ Expertise: {selectedStylist.expertise}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#1a1a2e]">Blog Post</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-[#ff6b6b] hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <img src={selectedPost.image} alt="Blog" className="w-full h-64 object-cover rounded-xl" />
              </div>
              
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-[#ff6b6b]/10 text-[#ff6b6b] text-xs rounded-full">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.categoryKey} 
                    value={defaultBlogContent[selectedPost.categoryKey]}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a2e] mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={defaultBlogContent[selectedPost.titleKey]}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500 pb-4 border-b border-gray-100 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={defaultBlogContent[selectedPost.dateKey]}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.readTimeKey} 
                    value={defaultBlogContent[selectedPost.readTimeKey]}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-[#1a1a2e]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={defaultBlogContent[selectedPost.authorKey]}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={defaultBlogContent[selectedPost.fullContentKey]}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-[#ff6b6b] text-white hover:bg-[#ff5252] transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes float-particle { 0%,100% { transform: translateY(0px) translateX(0px); opacity: 0; } 50% { transform: translateY(-100px) translateX(50px); opacity: 0.5; } }
        .animate-float-particle { animation: float-particle 8s ease-in-out infinite; }
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-10deg); } 60% { transform: scale(1.1) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        @keyframes slide-right { from { opacity: 0; transform: translateX(-50px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.8s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.8s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        @keyframes card-fly { 0% { opacity: 0; transform: translateX(-200px) translateY(100px) rotate(-15deg); } 60% { transform: translateX(10px) translateY(-10px) rotate(2deg); } 100% { opacity: 1; transform: translateX(0) translateY(0) rotate(0); } }
        .animate-card-fly { animation: card-fly 0.8s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(15px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        @keyframes float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-10px); } }
        .animate-slide-up { animation: slideUp 0.6s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
}